import { useLocation } from "react-router-dom";

import { ModelType } from "../../../../API";
import useLocationFromCache from "../../../../common/hooks/useLocationFromCache";
import { formatTimestamp } from "../../../../common/utils/timestampUtils";
import { errorNotification } from "../../../../common/variables/notification";
import { useSelectedModel } from "../../../../common/variables/selectedModel";
import useConvertArrayToCSV from "../../../../leak-finder/hooks/useConvertArrayToCSV";
import useCSVDownloader from "../../../../leak-finder/hooks/useCSVDownloader";
import { useGetRecordingsToExport } from "./useGetRecordingsToExport";
import { RouteEnum } from "../../../../common/models/enums";

interface EventRecordingsExportData {
  groupId: string;
  referenceId?: string;
  model: string;
  location: string;
  locationTags?: string;
  zone?: string;
  timestamp: string;
  rate?: string;
  cameraId: string;
  mediaOutput?: string;
}

const csvFileNames = new Map<string, string>([
  [ModelType.smoke, "smoke_events.csv"],
  [ModelType.fire, "fire_events.csv"],
  [ModelType.hardhat, "hardhat_events.csv"],
  [ModelType.gasleak, "gasleak_events.csv"],
  [ModelType.liquidleak, "liquidleak_events.csv"],
  ["", "recent_events.csv"],
]);

const useExportRecentEventsData = () => {
  const { pathname } = useLocation();

  const modelId = useSelectedModel();

  let model = modelId?.value?.toLowerCase() ?? "";

  if (pathname === RouteEnum.TelemetryHardHat) {
    model = "hardhat";
  }

  const { getRecordings, loading } = useGetRecordingsToExport();

  const { downloadCSV } = useCSVDownloader();

  const { convertArrayToCSV } = useConvertArrayToCSV();
  const { getCachedLocation } = useLocationFromCache();

  const defaultCsvColumns = [
    { field: "groupId", headerName: "Event ID" },
    { field: "referenceId", headerName: "Reference ID" },
    { field: "model", headerName: "Event Type" },
    { field: "location", headerName: "Location" },
    { field: "locationTags", headerName: "Location tags" },
    { field: "zone", headerName: "Zone" },
    { field: "timestamp", headerName: "Timestamp" },
    { field: "cameraId", headerName: "Device ID" },
    { field: "rate", headerName: "Rate (MCF/d)" },
    { field: "mediaOutput", headerName: "Media" },
  ];

  const securityColumns = [
    { field: "peopleCount", headerName: "People Count" },
    { field: "vehicleCount", headerName: "Vehicle Count" },
  ];

  // Rate (MCF/d) is only relevant for gasleak events or the general (unfiltered) table
  // where gasleak events may be present. Hide it for all other specific model filters.
  const isSpecificNonGasleakModel = model !== "" && model !== ModelType.gasleak;
  const columnsWithoutRate = defaultCsvColumns.filter(c => c.field !== "rate");

  let csvColumns: typeof defaultCsvColumns = isSpecificNonGasleakModel
    ? columnsWithoutRate
    : defaultCsvColumns;

  if (model === ModelType.gasleak) {
    csvColumns = defaultCsvColumns;
  }

  if (model === ModelType.security || model === ModelType.peopleandvehicle) {
    csvColumns = [...columnsWithoutRate, ...securityColumns];
  }

  const exportRecentEventsData = async () => {
    const recordings = await getRecordings();

    try {
      if (recordings.length > 0) {
        const updatedRows = recordings.map(row => {
          const newRow: EventRecordingsExportData = {
            groupId: row.groupId || "",
            model: row.modelId,
            location: row.rawEventInfo.location || row.facilityId,
            timestamp: row.timestamp,
            cameraId:
              row.rawEventInfo.data.camera_id ||
              row.rawEventInfo.data.instance_name ||
              "",
          };

          const cachedLocation = newRow.location?.includes("#L#")
            ? getCachedLocation(newRow.location ?? "")
            : null;

          const locationParts = cachedLocation?.name ?? newRow.location;

          if (!locationParts) {
            errorNotification("Location not found for event");
          }

          if (locationParts.length > 1) {
            newRow.location = locationParts;
          }

          newRow.referenceId = cachedLocation?.referenceId ?? "";

          newRow.locationTags =
            cachedLocation?.tags?.map(t => t.tagName).join(", ") ?? "";

          newRow.timestamp = formatTimestamp(newRow.timestamp);

          if (
            row.modelId === ModelType.gasleak &&
            row.rawEventInfo?.data.rate_mcf
          ) {
            const rateAsNumber = parseFloat(row.rawEventInfo.data.rate_mcf);

            newRow.rate = isNaN(rateAsNumber)
              ? ""
              : (rateAsNumber * 86400).toString();
          } else {
            newRow.rate = "";
          }

          if (row.rawEventInfo.data.base64Url) {
            newRow.mediaOutput = row.rawEventInfo.data.base64Url ?? "";
          }

          if (row.rawEventInfo?.data.zone_id) {
            newRow.zone = row.rawEventInfo.data.zone_id;
          } else {
            newRow.zone = "";
          }

          return newRow;
        });

        const rows = updatedRows.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

        const csvString = convertArrayToCSV(rows, csvColumns);

        const fileName = csvFileNames.get(model) ?? "recent_events.csv";

        downloadCSV(csvString, fileName);
      } else {
        errorNotification("No data available to export");
      }
    } catch (error) {
      errorNotification("Error exporting recent events data");

      console.error("Error exporting recent events data:", error);
    }
  };

  return { downloadRecentEventsData: exportRecentEventsData, loading } as const;
};

export default useExportRecentEventsData;
